import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useUser } from './useUser';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../../firebase/clientApp';
import PropertyDetailSchema from '../schemas/PropertyDetailSchema';
import RehabWorksheetSchema from '../schemas/RehabWorksheetSchema';
import getPathFromParams, {
  getCollectionPath,
} from '../utility/getPathFromParams';

const Context = createContext();

function useProperty() {
  const context = useContext(Context);
  if (context === undefined) {
    console.warn('useProperty must be used within PropertyContext');
    return {};
  }
  return context;
}

function PropertyContext({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [propertyData, setPropertyData] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [exists, setExists] = useState(true);

  // Use params to get path to property and scenario data
  const { user } = useUser();
  const params = useParams();

  const { pathname } = useLocation();
  const sharing = pathname.includes('sharing');

  const { pathToSource: propertyPath } = PropertyDetailSchema;
  const { pathToSource: scenarioPath } = RehabWorksheetSchema;

  const clearData = () => {
    setPropertyData(null);
    setScenarios(null);
  };

  // Subscribe to the property document
  useEffect(() => {
    const propertySource = getPathFromParams(propertyPath, params, user);
    const unsubscribeFromProperty = onSnapshot(
      doc(db, propertySource),
      (doc) => {
        const data = doc.data();
        setExists(doc.exists());
        if (!sharing || data?.shareAddress) {
          setPropertyData(data);
        } else {
          let propertyAddress = [];
          if (data?.propertyAddress && Array.isArray(data?.propertyAddress)) {
            data.propertyAddress.forEach((item) => {
              if (item.types.includes('administrative_area_level_1')) {
                propertyAddress.push(item);
              } else {
                item = { types: [] };
              }
            });
          }

          if (!propertyAddress.length) {
            propertyAddress = null;
          }

          setPropertyData({ ...data, propertyAddress });
        }
      },
      (error) => {
        setError('Error loading property data: ', error);
      }
    );

    // Clean up the listener
    return () => unsubscribeFromProperty();
  }, [params?.propertyId]);

  // Subscribe to the property scenarios collection
  useEffect(() => {
    const scenarioSource = getCollectionPath(scenarioPath, params, user);
    const scenarioQuery = query(
      collection(db, scenarioSource),
      orderBy('createdAt', 'asc')
    );
    const unsubscribeFromScenarios = onSnapshot(
      scenarioQuery,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => doc.data());
        setScenarios(data);
      },
      (error) => {
        setError('Error loading scenarios: ', error);
      }
    );

    // Clean up the listener
    return () => unsubscribeFromScenarios();
  }, [params?.propertyId]);

  // Check if all necessary data sources have been fetched
  useEffect(() => {
    const hasAllSources = !!propertyData && !!scenarios;
    if (hasAllSources) {
      setLoading(false);
      if (window.location.hostname === 'localhost') {
        console.log('-- Property data, ', propertyData);
        console.log('-- Scenarios, ', scenarios);
      }
    } else {
      setLoading(true);
    }
  }, [propertyData, scenarios]);

  const value = {
    loading,
    error,
    propertyData: propertyData || {},
    scenarios: scenarios || [],
    exists,
    clearData,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export { PropertyContext, useProperty };
